/**
 * AppHost-Adapter für den Fenstermanager (#504).
 *
 * Übersetzt das wirt-neutrale `AppHost`-Interface auf `useWindows()` und den
 * `WindowContext`. Wird im `WindowContainer` innerhalb des `WindowContext.Provider`
 * gemountet — also nur dort, wo der `WindowsProvider` garantiert existiert.
 * Apps sprechen ausschließlich `useAppHost()` an und wissen dadurch nicht, ob
 * sie in einem Fenster oder unter einer shell-freien Route laufen.
 *
 * Identitäts-Vertrag: Apps hängen den Host in Effekt-Dependencies (Untertitel,
 * Param-Sync). Er darf deshalb NICHT bei jeder Fensteränderung neu entstehen —
 * `updateWindowSubtitle` ändert die Fensterliste, und ein davon abhängiger Host
 * würde denselben Effekt sofort wieder auslösen. Die Fensterliste wandert
 * darum in eine Ref und bleibt aus den Dependencies heraus.
 */
import { useMemo, useRef, type ReactNode } from 'react'
import { useWindows } from '@/hooks/useWindows'
import { useWindowContext } from '@/types/window'
import { AppHostProvider, STANDALONE_ROUTES, searchFromParams, type AppHost } from '@/hooks/useAppHost'
import { useAllowedApplications, STANDALONE_APPLICATION } from '@/hooks/useAllowedApplications'
import type { AppId } from '@/data/apps'

export function WindowAppHost({ children }: { children: ReactNode }) {
  const { updateWindowSubtitle, updateWindowParams, openWindow, focusWindow, windows } =
    useWindows()
  const windowContext = useWindowContext()
  const windowId = windowContext?.windowId
  const params = windowContext?.params
  // `pdb` gate for the standalone routes (#746) — module-cached, so this costs
  // nothing beyond the first WindowAppHost that mounts.
  const { has: hasApplication } = useAllowedApplications()
  const hasStandaloneAccess = hasApplication(STANDALONE_APPLICATION)

  // Immer der aktuelle Stand, ohne die Host-Identität zu berühren.
  const windowsRef = useRef(windows)
  windowsRef.current = windows

  const host = useMemo<AppHost>(
    () => {
      // A window's appId never changes over its lifetime, so reading it off
      // `windowsRef` here (instead of adding `windows` to the deps below) is
      // safe — it only needs to be current as of whenever `windowId` itself
      // changes, same reasoning as `openApp`'s lookup below.
      // `windows[].appId` is a loose `string` (window store predates `AppId`
      // as a closed union); every entry is still populated from `AppId` at
      // creation time, so the cast just recovers what's already true.
      const currentAppId = windowsRef.current.find((w) => w.id === windowId)?.appId as
        | AppId
        | undefined
      const standaloneRoute = currentAppId ? STANDALONE_ROUTES[currentAppId] : undefined

      return {
        mode: 'window',
        // Die Fenster-ID IST hier die Instanz-Identität (#514).
        instanceId: windowId,
        params: params ?? {},
        setParams: (nextParams) => {
          if (!windowId) return
          updateWindowParams(windowId, nextParams)
        },
        setSubtitle: (subtitle) => {
          if (!windowId) return
          updateWindowSubtitle(windowId, subtitle)
        },
        openApp: (appId, appParams) => {
          // Ein bereits offenes Fenster derselben App fokussieren statt ein
          // zweites zu öffnen — sonst sammeln sich Duplikate an, und eine App,
          // die per Intent angesprochen wird (Drive → Communicator), verlöre
          // ihren Zustand.
          const existing = windowsRef.current.find((w) => w.appId === appId)
          if (existing) {
            focusWindow(existing.id)
            return
          }
          void openWindow(appId, undefined, undefined, appParams)
        },
        // Die Settings-App des Desktops kennt die App-Einstellungen nicht — die
        // App behält ihren eigenen Zugang.
        hostsAppSettings: false,
        canPopOut: true,
        popOut: (appId, title, popOutParams) => {
          void openWindow(appId, title, undefined, popOutParams)
        },
        canOpenStandalone: hasStandaloneAccess && !!standaloneRoute,
        openStandalone: () => {
          if (!standaloneRoute) return
          const query = searchFromParams(params ?? {})
          window.open(query ? `${standaloneRoute}?${query}` : standaloneRoute, '_blank', 'noopener,noreferrer')
        },
      }
    },
    [params, windowId, updateWindowSubtitle, updateWindowParams, openWindow, focusWindow, hasStandaloneAccess],
  )

  return <AppHostProvider host={host}>{children}</AppHostProvider>
}
